/*
 * ← Helpers para el filtro de mes/año del Resumen: tarjetas de
 * "Invertido" (costo de lo vendido) y "Ganancia" (ingresos - costo)
 * de un mes específico, su desglose de ventas por producto, y su
 * exportación a CSV. El dato viene de
 * Inventory.getMonthlySalesDetail(year, month), el mismo endpoint que
 * ya usaba el detalle de "Artículos vendidos" — ahora también trae
 * "cost"/"profit" por producto y un objeto "totals" del mes completo.
 */

import { ENV } from "../../../../../utils/constants";

const MONTH_NAMES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export function getMonthOptions() {
  return MONTH_NAMES.map((name, index) => ({
    value: index + 1,
    text: `${name.charAt(0).toUpperCase()}${name.slice(1)}`,
  }));
}

export function formatMonthTitle(year, month) {
  const name = MONTH_NAMES[Number(month) - 1] || "";

  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`;
}

export function formatCurrency(value) {
  return `Q ${(Number(value) || 0).toLocaleString("es-GT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getAttributeName(attribute) {
  if (attribute?.schema && typeof attribute.schema === "object") {
    return attribute.schema.name || "";
  }

  return attribute?.name || "";
}

function getAttributeValue(attribute) {
  if (
    attribute?.classification &&
    typeof attribute.classification === "object"
  ) {
    return (
      attribute.classification.name ||
      attribute.classification.value ||
      attribute.value ||
      ""
    );
  }

  return attribute?.label || attribute?.value || "";
}

export function getVariantLabel(attributes) {
  const list = Array.isArray(attributes) ? attributes : [];

  if (list.length === 0) {
    return "Variante única";
  }

  return list
    .map((attribute) => {
      const name = getAttributeName(attribute);
      const value = getAttributeValue(attribute);

      if (name && value) {
        return `${name}: ${value}`;
      }

      return value || name;
    })
    .filter(Boolean)
    .join(" · ");
}

export function getImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const value = String(imagePath);

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  return `${ENV.SERVER_HOST}/${value.replace(/\\/g, "/").replace(/^\/+/, "")}`;
}

// Desglose "cuánto vendimos y de qué" para el mes: mismos items que ya
// traía el endpoint (uno por producto+variante), ordenados por ingreso
// de mayor a menor, con el % que representa cada uno sobre el ingreso
// total del mes -- así se ve de un vistazo qué productos explican el
// total, no solo el total en sí.
export function getProductBreakdownRows(items = [], totals = {}) {
  const totalRevenue = Number(totals.revenue) || 0;

  return [...items]
    .sort((first, second) => (Number(second.revenue) || 0) - (Number(first.revenue) || 0))
    .map((item) => {
      const revenue = Number(item.revenue) || 0;

      return {
        key: `${item.productId}-${item.variantId}`,
        productName: item.productName || "Producto eliminado",
        productImage: getImageUrl(item.productImage),
        sku: item.sku || "—",
        variantLabel: getVariantLabel(item.attributes),
        quantity: Number(item.quantity) || 0,
        revenue,
        cost: Number(item.cost) || 0,
        revenueShare: totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0,
      };
    });
}

export function getMonthlySalesDetailResponse(result) {
  const body = result?.response || result || {};

  const items = Array.isArray(body.items)
    ? body.items
    : Array.isArray(result?.items)
    ? result.items
    : [];

  const rawTotals = body.totals || result?.totals || {};

  const totals = {
    unitsSold: Number(rawTotals.unitsSold) || 0,
    revenue: Number(rawTotals.revenue) || 0,
    cost: Number(rawTotals.cost) || 0,
    profit: Number(rawTotals.profit) || 0,
  };

  return { items, totals };
}

function escapeCsvValue(value) {
  const text = String(value ?? "");

  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

function buildCsvRow(values) {
  return values.map(escapeCsvValue).join(",");
}

export function buildMonthlySalesCsv(items = [], totals = {}, year, month) {
  const rows = [
    buildCsvRow([`Resumen ${formatMonthTitle(year, month)}`]),
    buildCsvRow([
      "Producto",
      "SKU",
      "Variante",
      "Cantidad",
      "Ingresos",
      "Costo",
      "Ganancia",
    ]),
  ];

  items.forEach((item) => {
    rows.push(
      buildCsvRow([
        item.productName || "",
        item.sku || "",
        getVariantLabel(item.attributes),
        item.quantity || 0,
        (Number(item.revenue) || 0).toFixed(2),
        (Number(item.cost) || 0).toFixed(2),
        (Number(item.profit) || 0).toFixed(2),
      ])
    );
  });

  rows.push(buildCsvRow([]));
  rows.push(
    buildCsvRow([
      "Total",
      "",
      "",
      totals.unitsSold || 0,
      (Number(totals.revenue) || 0).toFixed(2),
      (Number(totals.cost) || 0).toFixed(2),
      (Number(totals.profit) || 0).toFixed(2),
    ])
  );

  // ← BOM para que Excel abra los acentos/Q correctamente en Windows.
  return `﻿${rows.join("\r\n")}`;
}

export function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

export function buildExportFilename(year, month) {
  const monthText = String(month).padStart(2, "0");

  return `resumen-ventas-${year}-${monthText}.csv`;
}
